import { SelectField, TextField } from '~/common/components';
import { useI18n, Trans } from '~/common/context';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  Button,
  Card,
  CenteredSpinner,
  Col,
  FormContainer,
  FormFeedbackField,
  Row,
  Title,
} from '@wyz/ui';
import { mapIntoOptionItem, useSet } from '@wyz/utils';
import * as React from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { ISupplierTypeOptionItem, IUserCustomer } from '~/common/types';
import { CUSTOMER_LEVEL } from '@wyz/types';
import { PolicyItem, usePricingPolicyForm } from '~/purchase/components';
import {
  CommonPricingPolicyHelpers,
  QuotePricingPolicyHelpers,
} from '~/purchase/helpers';
import {
  PP_CUSTOM_CONSTRAINT,
  PRICING_STATUS_ENUM,
  PRICING_TYPE_ENUM,
  quotePricingConfigurationStepFormSchema,
} from '~/purchase/schemas';
import {
  IGlobalQuotePricingFormData,
  IPricingStatus,
  IQuotePricingConfigurationStepForm,
} from '~/purchase/types';
import { FEATURE_CONTEXT } from '~/common/schemas';
import { useManageEnums } from '~/common/api';
import { PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS } from '~/common/translators';
import { usePurchaseContext } from '~/purchase/context';

type CommonFormProps = {
  globalFormData: IGlobalQuotePricingFormData;
};
type PricingStepFormProps = CommonFormProps & {
  onSubmit: (
    data: IQuotePricingConfigurationStepForm,
    status: IPricingStatus,
  ) => void;
  targetCustomer?: IUserCustomer;
  supplierIds: string[];
  featureContext?: typeof FEATURE_CONTEXT.QUOTE_PRICING_POLICY_BRANDS;
};

function PricingStepFormContainer({
  onSubmit,
  globalFormData,
  targetCustomer,
  supplierIds,
  featureContext,
}: PricingStepFormProps) {
  const { t } = useI18n();
  const { state } = usePurchaseContext();

  const manageEnums = useManageEnums({
    getSupplierTypes: {
      enabled: true,
      args: {
        scope: 'PRICING_POLICY',
        customerIds:
          supplierIds.length !== 0
            ? supplierIds
            : [state.targetCustomer?.id as string],
      },
    },
  });
  const activeSupplierTypeOptions: ISupplierTypeOptionItem[] =
    mapIntoOptionItem(
      manageEnums.getSupplierTypes.query.data?.data ?? [],
      (item) => t(PRICING_POLICY_SUPPLIER_TYPE_TRANSLATIONS[item.value]),
      'codename',
      (item) => ({ id: item.value }),
    ) as ISupplierTypeOptionItem[];

  return (
    <>
      {manageEnums.getSupplierTypes.query.isLoading && <CenteredSpinner />}
      {manageEnums.getSupplierTypes.query.isSuccess && (
        <PricingStepForm
          onSubmit={onSubmit}
          globalFormData={globalFormData}
          targetCustomer={targetCustomer}
          supplierIds={supplierIds}
          featureContext={featureContext}
          activeSupplierTypeOptions={activeSupplierTypeOptions}
        />
      )}
    </>
  );
}

const PricingStepForm: React.FC<
  PricingStepFormProps & {
    activeSupplierTypeOptions: ISupplierTypeOptionItem[];
  }
> = ({
  onSubmit,
  globalFormData,
  targetCustomer,
  supplierIds,
  featureContext,
  activeSupplierTypeOptions,
}) => {
  const { t } = useI18n();
  const pricingPolicyForm = usePricingPolicyForm();
  const methods = useForm<IQuotePricingConfigurationStepForm>({
    defaultValues: globalFormData.PRICING,
    shouldUnregister: true,
    resolver: zodResolver(quotePricingConfigurationStepFormSchema),
  });

  const watchedVehicleTypeGroup = useWatch({
    control: methods.control,
    name: 'vehicleTypeGroup',
  });

  const { fields, append, remove } = useFieldArray<
    IQuotePricingConfigurationStepForm,
    'configurations',
    'key'
  >({
    name: 'configurations',
    keyName: 'key',
    control: methods.control,
  });
  const [activePolicies, activePoliciesHandlers] = useSet<string>(['0']);

  return (
    <FormContainer
      methods={methods}
      submitHandler={(formData, event) => {
        onSubmit(
          formData,
          ((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute(
            'data-status',
          ) as IPricingStatus) ?? PRICING_STATUS_ENUM.DRAFT,
        );
      }}
      formConfiguration={{ mode: pricingPolicyForm.mode }}
      id={QuotePricingPolicyHelpers.QUOTE_PRICING_POLICY_STEP_INDEX.PRICING.toString()}
    >
      <Card>
        <Card.Header>
          <Card.Title>
            <Title heading='xl' weight='bold'>
              <Trans i18nKey='app.quote.pricing_policy.form.step1.title' />
            </Title>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Row className='mt-3'>
            <Col>
              <TextField
                name='name'
                label={<Trans i18nKey='app.common.pricing_policy.name' />}
                placeholder={t(
                  'app.quote.pricing_policy.form.step1.name_placeholder',
                )}
                type='text'
                maxLength={PP_CUSTOM_CONSTRAINT.NAME_MAX_LENGTH}
                size='sm'
                required
              />
            </Col>
            <Col>
              <SelectField
                name='vehicleTypeGroup'
                label={
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.vehicle_type_group' />
                }
                options={pricingPolicyForm.vehicleTypeGroups.optionItems}
                size='sm'
                required
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Accordion.Container
              activeKey={Array.from(activePolicies)}
              onSelect={(selectedPolicies) => {
                activePoliciesHandlers.set(selectedPolicies as Array<string>);
              }}
              className='space-y-2'
              alwaysOpen
            >
              {fields.map((field, index) => (
                <PolicyItem
                  key={field.key}
                  policyItemIndex={index}
                  onDuplicate={(index) =>
                    append(methods.getValues('configurations')[index])
                  }
                  onRemove={remove}
                  isOpened={activePolicies.includes(index.toString())}
                  parameters={{
                    selectedBrandIds:
                      (targetCustomer?.quoteBrandTemplate?.brands ?? []).map(
                        (item) => item.externalId.id,
                      ) ?? [],
                    featureContext: featureContext,
                  }}
                  hiddenFields={{
                    supplierTypes:
                      targetCustomer &&
                      targetCustomer.level >= CUSTOMER_LEVEL.N2,
                  }}
                  supplierIds={supplierIds}
                  activeSupplierTypeOptions={activeSupplierTypeOptions}
                />
              ))}
            </Accordion.Container>
          </Row>
          {pricingPolicyForm.mode === 'WRITE' && (
            <Row className='mt-3'>
              <Col>
                <Button
                  id='add-policy-item'
                  disabled={!watchedVehicleTypeGroup}
                  variant={
                    methods.formState.errors.configurations?.message
                      ? 'danger'
                      : 'primary'
                  }
                  onClick={() => {
                    append({
                      brands: [],
                      supplierTypes: activeSupplierTypeOptions.map(
                        (item) => item.value,
                      ),
                      configurationType: PRICING_TYPE_ENUM.DISCOUNT_BF,
                      configurationTypeValue: null,
                      name: t('app.pricing_policy.form.price_block', {
                        number: fields.length + 1,
                      }),
                      refinementFlags: {
                        diameter: false,
                        season: false,
                        supplierType: false,
                        tyreType: false,
                      },
                      definition: {
                        supplierTypes: {
                          [CommonPricingPolicyHelpers.NO_REFINEMENT_VALUE]: {
                            refinements: [{ seasons: {}, diameter: null }],
                          },
                        },
                      },
                    });
                    activePoliciesHandlers.add(fields.length.toString());
                  }}
                >
                  <Trans i18nKey='app.quote.pricing_policy.form.step1.add_price_block' />
                </Button>
              </Col>{' '}
              <FormFeedbackField name='configurations' translate={t} />
            </Row>
          )}
        </Card.Body>
      </Card>
    </FormContainer>
  );
};

export default PricingStepFormContainer;
